import { useState, type ReactElement, type ReactNode } from 'react';
import { Drawer, useAdmin, type AdminCtx } from '../ui/Shell';
import { Banner, DateRange, Kv, ListMore, Pill, Table, type Row, type SortState } from '../ui/Block';
import { ago, fmtBytes, fmtDate } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { countOf, qs, usePagedList, type Paged } from '../lib/list';
import { DBlock, DrawerBody, finPill, kindLabel, langName, shortId } from './dataShared';
import { ActButtons, UserProfile, countsCell, errOf, keyCols, keyRows, pl, stateCell, suspendActs, type ApiKey, type Counts, type Principal } from './Users';
import { AccountPanel } from './BillingAccounts';
import { UsageDrawer, UsagePanel } from './UsageDrawer';
import { BroadcastDrawer, type Broadcast } from './BroadcastDrawer';
import { TranscriptionDrawer, type Transcription } from './TranscriptionDrawer';
import { usd } from './billingShared';
import { FingerprintTab } from './CustomerFingerprint';
import { CustomerCosts } from './CustomerCosts';
import type { CostCols } from './costShared';

export interface Customer extends Principal {
  principalId: string; kind: 'user' | 'org'; name: string; userId: string | null; email: string | null; displayName: string | null; workosUserId: string | null;
  orgs: string | null; members: number | null; createdAt: string; lastActiveAt: string | null;
  billing: null | { planId: string | null; balance: number; hasPaid: boolean; lastPaymentAt: string | null; pendingChange: boolean; subscriptionStatus: string | null };
  costs?: CostCols | null;
}
interface CustomerDetail extends Customer { counts: Counts; bytes: number; minutes30d: number }
interface AuditEvent { id: string; action: string; targetType: string | null; targetId: string | null; at: string; staff: boolean; actor: string | null; ip: string | null }

export type CustomerTab = 'profile' | 'billing' | 'keys' | 'usage' | 'costs' | 'content' | 'audit' | 'fingerprint';
const TABS: { key: CustomerTab; label: string }[] = [
  { key: 'profile', label: 'Profile' }, { key: 'billing', label: 'Billing' }, { key: 'keys', label: 'Keys' },
  { key: 'usage', label: 'Usage' }, { key: 'costs', label: 'Costs' }, { key: 'content', label: 'Content' }, { key: 'audit', label: 'Audit' }, { key: 'fingerprint', label: 'Fingerprint' },
];
export const isCustomerTab = (k?: string): k is CustomerTab => TABS.some((t) => t.key === k);

export const KEY_STATES = [{ value: '', label: 'Any state' }, { value: 'active', label: 'Active' }, { value: 'suspended', label: 'Suspended' }, { value: 'unused', label: 'Never used' }, { value: 'revoked', label: 'Revoked' }];

export const customerName = (c: Customer) => (c.kind === 'org' ? c.name : c.email || c.name || '(no email)');

export const customerKey = (id: string, tab: CustomerTab = 'profile') => (tab === 'profile' ? `customer:${id}` : `customer:${id}:${tab}`);
export function openCustomer(ctx: AdminCtx, id: string, tab: CustomerTab = 'profile', push?: boolean) {
  ctx.openDrawer(customerKey(id, tab), <CustomerDrawer id={id} tab={tab} />, push);
}

type Frame = (acts: ReactNode, body: ReactNode) => ReactElement;

export function CustomerDrawer({ id, tab = 'profile' }: { id: string; tab?: CustomerTab }) {
  const ctx = useAdmin();
  const d = useData<CustomerDetail>(ctx.env, `/admin/customers/${id}`, 30, ctx.paused);
  const c = d.data;
  const switchTab = (t: CustomerTab) => {
    if (t === tab) return;
    ctx.swapDrawer(customerKey(id, t), <CustomerDrawer id={id} tab={t} />);
  };

  const strip = (
    <div className="blk" style={{ height: 'auto', padding: 0, flex: 'none' }}>
      <div className="blk__tabs" role="tablist" style={{ borderTop: 0, overflowX: 'auto' }}>
        {TABS.map((t) => {
          const n = !c ? undefined : t.key === 'keys' ? c.counts.keys : t.key === 'content' ? c.counts.broadcasts + c.counts.transcriptions : undefined;
          return (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`blk__tab ${tab === t.key ? 'blk__tab--on' : ''}`} onClick={() => switchTab(t.key)}>
              {t.label}{n ? <span className="pill" style={{ padding: '1px 5px', borderRadius: 4, background: 'var(--fill-08)', color: 'var(--ink-62)' }}>{n.toLocaleString()}</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
  const frame: Frame = (acts, body) => (
    <Drawer wide eyebrow={`CUSTOMER${c ? ` · ${c.kind === 'org' ? 'ORG' : 'USER'}` : ''}`} title={c ? customerName(c) : '…'}
      sub={c ? (c.kind === 'org' ? `${pl(c.members ?? 0, 'member')}` : [c.displayName, c.orgs].filter(Boolean).join(' · ') || undefined) : undefined}
      chip={c?.billing ? <Pill tone={c.billing.balance <= 0 ? 'bad' : 'ok'}>{usd(c.billing.balance)}</Pill> : undefined}
      acts={acts || undefined}>
      <DrawerBody>{strip}{body}</DrawerBody>
    </Drawer>
  );

  if (!c) {
    const missing = d.error?.status === 404;
    return frame(null, missing ? <DBlock title="Customer" empty="No customer with this id." /> : <DBlock title="Customer" loading={d.loading} error={errOf(d)} />);
  }
  const p = c.principalId;
  return (
    <>
      {tab === 'profile' && (c.kind === 'user' && c.userId ? <UserProfile id={c.userId} frame={frame} onChange={d.reload} /> : <OrgProfile c={c} frame={frame} reload={d.reload} />)}
      {tab === 'billing' && <AccountPanel principalId={p} frame={frame} onChange={d.reload} />}
      {tab === 'keys' && <KeysTab principalId={p} frame={frame} />}
      {tab === 'usage' && <UsagePanel kind="principal" id={p} frame={(acts, body) => frame(acts, body)} />}
      {tab === 'costs' && <CustomerCosts principalId={p} frame={frame} />}
      {tab === 'content' && <ContentTab principalId={p} frame={frame} />}
      {tab === 'audit' && <AuditTab principalId={p} frame={frame} />}
      {tab === 'fingerprint' && <FingerprintTab principalId={p} frame={frame} onOpen={(o) => openCustomer(ctx, o, 'fingerprint', true)} />}
    </>
  );
}

function OrgProfile({ c, frame, reload }: { c: CustomerDetail; frame: Frame; reload: () => void }) {
  const ctx = useAdmin();
  const o = useData<{ orgs: { id: string; members: string | null }[] }>(ctx.env, `/admin/orgs${qs({ q: c.principalId, limit: 1 })}`, 0, true);
  const members = o.data?.orgs.find((x) => x.id === c.principalId)?.members ?? null;
  const acts = <ActButtons mobile={ctx.mobile} acts={suspendActs(ctx, 'principal', c.principalId, c.name, c, reload, `Every API key of ${c.name} and every member acting for it get 403 from the next request.`)} />;
  return frame(acts, (
    <>
      {c.deletedAt && <DBlock bg="var(--conf-low-soft)"><Banner icon="warn" fg="var(--conf-low)" text="Deleted" sub={`Since ${fmtDate(c.deletedAt)}${c.purgeAfter ? ` · due for purge ${fmtDate(c.purgeAfter)}` : ''}`} /></DBlock>}
      {c.suspendedAt && !c.deletedAt && <DBlock bg="var(--conf-low-soft)"><Banner icon="warn" fg="var(--conf-low)" text="Suspended" sub={`Since ${fmtDate(c.suspendedAt)} · ${c.suspendedReason ?? ''} · requests answer 403 suspended`} /></DBlock>}
      <DBlock title="Organisation">
        <Kv rows={[
          { k: 'Name', v: { t: c.name, fw: 600 } },
          { k: 'Principal id', v: { t: c.principalId, mono: true } },
          { k: 'Members', v: { t: members ?? (o.loading ? '…' : '—'), sub: pl(c.members ?? 0, 'member') } },
          { k: 'State', v: stateCell(c) },
          { k: 'Created', v: { t: fmtDate(c.createdAt) } },
          { k: 'Last active', v: { t: c.lastActiveAt ? fmtDate(c.lastActiveAt) : '—', sub: 'latest member sign-in' } },
          { k: 'Owns', v: countsCell(c.counts) },
          { k: 'Storage', v: { t: fmtBytes(c.bytes), mono: true } },
          { k: 'Minutes, 30 days', v: { t: Math.round(c.minutes30d).toLocaleString(), mono: true } },
        ]} />
      </DBlock>
    </>
  ));
}

function useSort(initial: SortState) {
  const [sort, setSort] = useState<SortState>(initial);
  return { sort, onSort: (key: string, first: 'asc' | 'desc') => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: first })) };
}

function KeysTab({ principalId, frame }: { principalId: string; frame: Frame }) {
  const ctx = useAdmin();
  const [state, setState] = useState('');
  const s = useSort({ key: 'created', dir: 'desc' });
  const l = usePagedList<ApiKey, Paged & { keys: ApiKey[] }>(ctx.env, `/admin/api-keys${qs({ principal: principalId, state, sort: s.sort.key, dir: s.sort.dir })}`, 'keys', 30, ctx.paused);
  const err = describeErr(l.error);
  return frame(null, (
    <DBlock title="API keys" count={countOf(l)} asOf={hhmmss(l.at)} stale={l.ageS > 60} loading={l.loading && !l.data} error={err ? { ...err, retry: l.reload } : null}
      selects={[{ value: state, options: KEY_STATES, onChange: setState, title: 'State' }]}
      empty={l.data && !l.rows.length ? (state ? 'No key in this state.' : 'No API keys.') : undefined}
      foot={<ListMore list={l} noun="keys" />}>
      <Table mobile={ctx.mobile} minWidth={1000} cols={keyCols(false)} sort={s.sort} onSort={s.onSort}
        rows={keyRows(ctx, l.rows, l.reload, false, (k) => ctx.openDrawer(`usage:key:${k.id}`, <UsageDrawer kind="key" id={k.id} />, true))} />
    </DBlock>
  ));
}

function ContentTab({ principalId, frame }: { principalId: string; frame: Frame }) {
  const ctx = useAdmin();
  const { env, paused, mobile } = ctx;
  const bs = useSort({ key: 'activity', dir: 'desc' });
  const ts = useSort({ key: 'created', dir: 'desc' });
  const b = usePagedList<Broadcast, Paged & { broadcasts: Broadcast[] }>(env, `/admin/broadcasts${qs({ owner: principalId, sort: bs.sort.key, dir: bs.sort.dir })}`, 'broadcasts', 30, paused);
  const t = usePagedList<Transcription, Paged & { transcriptions: Transcription[] }>(env, `/admin/transcriptions${qs({ owner: principalId, sort: ts.sort.key, dir: ts.sort.dir })}`, 'transcriptions', 30, paused);
  const bErr = describeErr(b.error);
  const tErr = describeErr(t.error);
  const bRows: Row[] = b.rows.map((x) => ({
    key: x.id, onClick: () => ctx.openDrawer(`bc:${x.id}`, <BroadcastDrawer id={x.id} />, true),
    cells: [
      { t: x.code, mono: true, fw: 600, sub: x.name },
      { pill: { label: x.access.toUpperCase(), tone: x.access === 'public' ? 'info' : 'mute' } },
      x.currentRecording ? { pill: { label: 'ON AIR', tone: 'live' } } : x.lifecycle === 'archived' ? { t: 'archived', fg: 'var(--ink-55)' } : { t: 'off air', fg: 'var(--ink-45)' },
      { t: x.lastRecordedAt ? ago(x.lastRecordedAt) : 'never', fg: x.lastRecordedAt ? undefined : 'var(--ink-45)' },
    ],
  }));
  const tRows: Row[] = t.rows.map((x) => ({
    key: x.id, onClick: () => ctx.openDrawer(`tx:${x.id}`, <TranscriptionDrawer id={x.id} />, true),
    cells: [
      { t: x.name, fw: 600, sub: <span className="mono">{shortId(x.id)}{x.lifecycle === 'archived' ? ' · archived' : ''}</span> },
      { pill: { label: kindLabel(x), tone: x.live ? 'info' : 'mute' } },
      { t: x.spokenLanguage ? langName(x.spokenLanguage) : '—', fg: x.spokenLanguage ? undefined : 'var(--ink-40)' },
      x.finalize ? { pill: finPill(x.finalize, x.job === 'failed' || x.recording?.endReason === 'failed') } : { t: '—', fg: 'var(--ink-40)' },
      { t: fmtDate(x.createdAt) },
    ],
  }));
  return frame(null, (
    <>
      <DBlock title="Broadcasts" count={countOf(b)} asOf={hhmmss(b.at)} loading={b.loading && !b.data} error={bErr ? { ...bErr, retry: b.reload } : null}
        empty={b.data && !b.rows.length ? 'No broadcasts.' : undefined} foot={<ListMore list={b} noun="broadcasts" />}>
        <Table mobile={mobile} minWidth={560} rows={bRows} sort={bs.sort} onSort={bs.onSort} cols={[{ label: 'CODE', width: 'minmax(170px,1.5fr)', sort: 'code', sortAsc: true }, { label: 'ACCESS', width: '84px', sort: 'access', sortAsc: true }, { label: 'ON AIR', width: '96px', sort: 'air', sortAsc: true }, { label: 'LAST USED', width: 'minmax(96px,.8fr)', sort: 'activity' }]} />
      </DBlock>
      <DBlock title="Transcriptions" count={countOf(t)} asOf={hhmmss(t.at)} loading={t.loading && !t.data} error={tErr ? { ...tErr, retry: t.reload } : null}
        empty={t.data && !t.rows.length ? 'No transcriptions.' : undefined} foot={<ListMore list={t} noun="transcriptions" />}>
        <Table mobile={mobile} minWidth={640} rows={tRows} sort={ts.sort} onSort={ts.onSort} cols={[{ label: 'TRANSCRIPTION', width: 'minmax(190px,2fr)', sort: 'name', sortAsc: true }, { label: 'KIND', width: '70px', sort: 'kind', sortAsc: true }, { label: 'LANGUAGE', width: 'minmax(90px,.8fr)', sort: 'lang', sortAsc: true }, { label: 'FINALIZE', width: '96px', sort: 'finalize', sortAsc: true }, { label: 'CREATED', width: '110px', sort: 'created' }]} />
      </DBlock>
    </>
  ));
}

function AuditTab({ principalId, frame }: { principalId: string; frame: Frame }) {
  const ctx = useAdmin();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const s = useSort({ key: 'at', dir: 'desc' });
  const l = usePagedList<AuditEvent, Paged & { events: AuditEvent[] }>(ctx.env, `/admin/audit${qs({ principal: principalId, from, to, sort: s.sort.key, dir: s.sort.dir })}`, 'events', 30, ctx.paused);
  const err = describeErr(l.error);
  const rows: Row[] = l.rows.map((e) => ({
    key: e.id,
    cells: [
      { t: fmtDate(e.at), mono: true },
      { t: e.action, mono: true, fw: 500 },
      { t: e.targetType ?? '—', sub: e.targetId ? <span className="mono">{e.targetId}</span> : undefined },
      e.staff ? { pill: { label: 'STAFF', tone: 'warn' }, sub: e.actor ?? undefined } : { t: e.actor ?? '—', sub: e.ip ?? undefined, fg: e.actor ? undefined : 'var(--ink-45)' },
    ],
  }));
  return frame(null, (
    <DBlock title="Audit trail" count={countOf(l)} asOf={hhmmss(l.at)} stale={l.ageS > 60} loading={l.loading && !l.data} error={err ? { ...err, retry: l.reload } : null}
      extra={<DateRange label="When" from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />}
      empty={l.data && !l.rows.length ? 'Nothing logged for this customer.' : undefined} foot={<ListMore list={l} noun="events" />}>
      <Table mobile={ctx.mobile} minWidth={640} rows={rows} sort={s.sort} onSort={s.onSort} cols={[{ label: 'WHEN', width: '120px', sort: 'at' }, { label: 'ACTION', width: 'minmax(160px,1.2fr)', sort: 'action', sortAsc: true }, { label: 'TARGET', width: 'minmax(180px,1.5fr)', sort: 'targetType', sortAsc: true }, { label: 'BY', width: 'minmax(140px,1fr)', sort: 'actor', sortAsc: true }]} />
    </DBlock>
  ));
}
